import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PhoneField } from "@/components/PhoneField";
import { MaskedInput } from "@/components/ui/masked-input";
import { StateSelect } from "@/components/ui/state-select";
import { insertCustomerSchema, type InsertCustomer } from "@shared/schema";

type CustomerFormData = InsertCustomer;

interface CustomerFormProps {
  onSubmit: (data: CustomerFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  form?: UseFormReturn<CustomerFormData>;
}

export function CustomerForm({
  onSubmit,
  onCancel,
  isSubmitting = false,
  form: externalForm,
}: CustomerFormProps) {
  // Use external form if provided, otherwise create internal form
  const { t } = useTranslation();
  const internalForm = useForm<CustomerFormData>({
    resolver: zodResolver(insertCustomerSchema),
    defaultValues: {
      customerType: "person",
      firstName: "",
      lastName: "",
      businessName: "",
      email: "",
      phone: "",
      streetAddress: "",
      streetAddress2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "US",
    },
  });

  const form = externalForm || internalForm;

  // Watch customer type to conditionally render fields
  const customerType = form.watch("customerType");

  // Clear name fields when switching customer types (preserve all other fields)
  useEffect(() => {
    // When switching to business, clear person name fields
    if (customerType === "business") {
      form.setValue("firstName", "");
      form.setValue("lastName", "");
    }
    // When switching to person, clear business name field
    else if (customerType === "person") {
      form.setValue("businessName", "");
    }
  }, [customerType]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Customer Type Selection */}
        <FormField
          control={form.control}
          name="customerType"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>{t("components.customers.customerForm.customerType")}</FormLabel>
              <FormControl>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={field.value === "person" ? "default" : "outline"}
                    className="flex-1 rounded-full"
                    onClick={() => field.onChange("person")}
                  >
                    {t("components.customers.customerForm.individual")}
                  </Button>
                  <Button
                    type="button"
                    variant={field.value === "business" ? "default" : "outline"}
                    className="flex-1 rounded-full"
                    onClick={() => field.onChange("business")}
                  >
                    {t("components.customers.customerForm.business")}
                  </Button>
                </div>
              </FormControl>
              <FormDescription>
                {t("components.customers.customerForm.youCanChangeBetween")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Conditional Name Fields */}
        {customerType === "person" ? (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("components.customers.customerForm.firstName")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("components.customers.customerForm.enterFirstName")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("components.customers.customerForm.lastName")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("components.customers.customerForm.enterLastName")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ) : (
          <FormField
            control={form.control}
            name="businessName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("components.customers.customerForm.businessName")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("components.customers.customerForm.enterBusinessName")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("components.customers.customerForm.email")}</FormLabel>
              <FormControl>
                <Input type="email" placeholder={t("components.customers.customerForm.enterEmail")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("components.customers.customerForm.phone")}</FormLabel>
              <FormControl>
                <PhoneField
                  value={field.value || ""}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="streetAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("components.customers.customerForm.streetAddress")}</FormLabel>
              <FormControl>
                <Input placeholder={t("components.customers.customerForm.123MainSt")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="streetAddress2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("components.customers.customerForm.streetAddress2Optional")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("components.customers.customerForm.aptSuiteEtc")}
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("components.customers.customerForm.city")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("components.customers.customerForm.city")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("components.customers.customerForm.state")}</FormLabel>
                <FormControl>
                  <StateSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={t("components.customers.customerForm.selectState")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="zipCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("components.customers.customerForm.zipCode")}</FormLabel>
              <FormControl>
                <MaskedInput
                  mask="zipcode"
                  placeholder="12345"
                  value={field.value}
                  onValueChange={(cleaned: string) => {
                    field.onChange(cleaned);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("components.customers.customerForm.creating") : t("components.customers.customerForm.createCustomer")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
